<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual, orderBy } from 'lodash-es'
    import { DateTime } from 'luxon'
    import { toASCII, toUnicode } from 'punycode-esm'

    import { gettext as _, resolve, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import Date from 'hydria/components/Date.svelte'
    import Modal from 'hydria/components/Modal.svelte'
    import PageList from 'hydria/components/PageList.svelte'

    const LIMIT = 99999
    const DELAY_MS = 1250
    type Status = 'pending' | 'failed' | 'done' | 'canceled'
    type StatusEmp = '' | 'pending' | 'failed' | 'done' | 'canceled'
    const statuses: StatusEmp[] = ['', 'pending', 'failed', 'done', 'canceled']
    const statusNames = { pending: 'En attente', failed: 'En erreur', done: 'Terminé', canceled: 'Annulé' }

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        skip: 'int1+',

        text: 'string',
        period: 'int1+',
        status: 'string',
    }
    const mappingOrder = ['skip']

    // Search options for the backend
    interface SOptions {
        skip: number

        text: string
        period: number
        status: Status | ''
    }
    const defaults: SOptions = {
        skip: 0,
        text: '',
        period: 0,
        status: '',
    }

    interface TransferItem {
        id: string
        idna: string
        owner: string
        stamp: DateTime
        system: 'opensrs' | 'cira' | 'webnames'
        status: Status
        status_extra: string
        auth_password: string
        extra_years: number
        last_check?: DateTime
        comment?: string
    }

    // Search result from the server
    interface ISearchResult {
        items: Array<TransferItem>
        total_items: number
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        if (options.skip && options.skip == loadedOptions.skip) {
            // Something non-skip must have changed, so we force skip to 0
            options.skip = 0
        }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/transfers' : `/transfers?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/transfers' : `/transfers?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    let results = writable([] as any[])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = { limit: LIMIT }
        if (sOptions.skip > 0) sdata.skip = sOptions.skip
        for (const [field, value] of Object.entries(sOptions)) {
            if (['skip'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (field == 'hidden') {
                        sdata[field] = value == 'visible' ? false : value == 'hidden' ? true : null
                    } else if (value != null && value != '') sdata[field] = value
                    break
                case 'number':
                case 'int1+':
                    if (value != null && value != '' && value != 0) sdata[field] = value
                    break
            }
        }

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/domain/1/transfers', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = $state(0)

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []
        const now = DateTime.now()

        data.total_items = data.items.length
        nItems = data.total_items

        for (const item of data.items) {
            for (const key of ['stamp', 'last_check']) {
                if (item[key]) item[key] = toLuxon(item[key])
            }

            litems.push(item)
        }

        results.set(orderBy(litems, [(o) => o.stamp], ['asc']))
        if (loading) loading = false
    }

    let editComment: undefined | TransferItem = $state(undefined)
    let commentText: string = $state('')
    let commentSaving: boolean = $state(false)
    let commentElement: HTMLTextAreaElement | undefined = $state(undefined)

    function openEditComment(item: TransferItem) {
        commentElement = undefined
        commentText = item.comment ? item.comment : ''
        editComment = item
    }
    function closeComment() {
        commentElement = undefined
        editComment = undefined
        commentText = ''
    }
    async function saveComment() {
        let r: SimpleFetchResult
        let i = -1
        for (let [li, litem] of $results.entries()) {
            if (litem.id == editComment?.id) {
                i = li
                break
            }
        }
        if (i < 0) return
        commentSaving = true

        try {
            r = await simpleFetch('POST', `/json/domain/1/transfer/${$results[i].id}/comment`, { old_comment: $results[i].comment, comment: commentText })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.success) {
            $results[i].comment = commentText
        }
        closeComment()
        commentSaving = false
    }

    let editStatus: undefined | TransferItem = $state(undefined)
    let newStatus: string = $state('')
    let statusSaving: boolean = $state(false)
    let statusElement: HTMLTextAreaElement | undefined = $state(undefined)

    function openEditStatus(item: TransferItem) {
        statusElement = undefined
        newStatus = item.status ? item.status : ''
        editStatus = item
    }
    function closeStatus() {
        statusElement = undefined
        editStatus = undefined
        newStatus = ''
    }
    async function saveStatus() {
        let r: SimpleFetchResult
        let i = -1
        for (let [li, litem] of $results.entries()) {
            if (litem.id == editStatus?.id) {
                i = li
                break
            }
        }
        if (i < 0) return
        statusSaving = true

        try {
            r = await simpleFetch('PUT', `/json/domain/1/transfer/${$results[i].id}/status/${newStatus}`, { old_status: $results[i].status })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.success) {
            $results[i].status = r.data.new_status
            if (r.data.import_status == 'imported') {
                notify({ title: 'Importation', text: `Le domaine ${$results[i].idna} a été importé`, type: 'success' })
            } else if (r.data.import_status == 'failed') {
                notify({ title: 'Importation', text: `Le domaine ${$results[i].idna} n’a pas été importé`, type: 'danger' })
            }
        }
        closeStatus()
        statusSaving = false
    }

    // svelte-ignore state_referenced_locally
    let text = writable(sOptions.text)

    function loadIntoForm() {
        $text = sOptions.text
    }

    let textTimer: ReturnType<typeof setTimeout> | undefined = undefined

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = undefined
        }
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue == sOptions.text) {
                return
            }
            textTimer = setTimeout(() => {
                sOptions.text = newValue
                textTimer = undefined
                setQueryString(sOptions)
            }, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.text = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })

    let daysChoices = [0, 7, 14, 21, 60]
    let textIsFocussed: boolean = $state(false)
</script>

<style>
    .btn-half {
        opacity: 0.5;
    }
    td {
        vertical-align: middle;
    }
</style>

<Breadcrumb crumbs={[{ txt: tgettext('Transferts') }]} />

<div class="card mb-4">
    <div class="card-title text-primary">
        <div>{$_('Search parameters')}</div>
    </div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
                submit()
            }}
            class="form"
        >
            <div class="d-flex flex-wrap gap-2">
                <div>
                    <div class="input-group">
                        <input type="text" class="form-control" bind:this={mainInput} bind:value={$text} onfocus={() => (textIsFocussed = true)} onblur={() => (textIsFocussed = false)} />
                        {#if $text}
                            <button
                                class="btn btn-secondary"
                                type="button"
                                disabled={updating}
                                onclick={() => {
                                    $text = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                    </div>
                </div>
                <div>
                    <div class="btn-group">
                        <button type="button" class="btn btn-outline-secondary" disabled>Statut :</button>
                        {#each statuses as status}
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.status == status}
                                onclick={() => {
                                    sOptions.status = sOptions.status == status ? '' : status
                                    setQueryString(sOptions)
                                }}
                                >{#if !status}<Icon inline icon="mdi:asterisk" />{:else}{statusNames[status]}{/if}</button
                            >
                        {/each}
                    </div>
                </div>
                <div>
                    <div class="btn-group">
                        <button type="button" class="btn btn-outline-secondary" disabled>Période (jours) :</button>
                        {#each daysChoices as ndays}
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.period == ndays}
                                onclick={() => {
                                    sOptions.period = sOptions.period == ndays ? 0 : ndays
                                    setQueryString(sOptions)
                                }}
                                >{#if !ndays}<Icon inline icon="mdi:infinity" />{:else}{ndays}{/if}</button
                            >
                        {/each}
                    </div>
                </div>
                <div class="ps-2">
                    <div class="float-end">
                        <button class="btn btn-primary" type="submit" disabled={updating || commentSaving}>{$_('Filter')}</button>
                    </div>
                </div>
            </div>
            <div>
                <div class="help-text text-muted">
                    <div class:text-primary={textIsFocussed}><code class:text-muted={!textIsFocussed}>%</code> pour “n’importe quoi 0 ou plus fois”, <code class:text-muted={!textIsFocussed}>_</code> pour “n’importe quel caractère”.</div>
                    <div class:text-primary={!sOptions.period && !sOptions.text && !sOptions.status}>Sans aucune recherche: 21 jours <em>ou</em> « En attente ».</div>
                </div>
            </div>
        </form>
    </div>
</div>

{#if loading}
    <div class="loading">
        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
        {$_('Loading…')}
    </div>
{:else}
    <div class="card">
        <div class="card-title text-primary">{$_('Transferts')}</div>

        {#if $results.length}
            <div class="card-body">
                <table class="table table-striped mb-0" class:disabled-look={updating}>
                    <thead>
                        <tr>
                            <th>Domaine</th>
                            <th class="col-md-1">Statut</th>
                            <th class="col-md-2">Utilisateur</th>
                            <th class="col-md-1">Lancement</th>
                            <th class="col-md-1"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $results as item (item.id)}
                            <tr class:info={item.status == 'pending'} class:danger={item.status == 'failed' || item.status == 'canceled'} class:warning={item.status == 'done'}>
                                <td>{toUnicode(item.idna)}</td>
                                <td>
                                    {#if item.status == 'pending'}
                                        <button
                                            type="button"
                                            class="btn btn-link btn-a"
                                            onclick={() => {
                                                openEditStatus(item)
                                            }}>{statusNames[item.status]}</button
                                        >
                                    {:else}
                                        {item.status}
                                    {/if}
                                </td>
                                <td><a use:sanchor href={spath(`/wdom-user/${item.owner}`)}>{item.name}</a></td>
                                <td style="white-space: nowrap"><Date value={item.stamp} format="h-table-s" /></td>
                                <td>
                                    <button type="button" class="btn btn-sm btn-outline-secondary" class:btn-half={!item.comment} data-tooltip={item.comment} onclick={() => openEditComment(item)}>
                                        <Icon inline icon="mdi:comment-text-outline" />
                                    </button>
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
        <div class="card-footer">
            <PageList
                loading={updating || commentSaving}
                {nItems}
                limit={LIMIT}
                skip={sOptions.skip}
                setSkip={(e) => {
                    sOptions.skip = e
                    setQueryString(sOptions)
                }}
            />
        </div>
    </div>
{/if}

{#if editComment}
    <Modal
        close={() => closeComment()}
        keep={() => saveComment()}
        modalClasses="modal-xl"
        mounted={() => {
            commentElement?.focus()
        }}
    >
        <div class="modal-header text-bg-primary">Commentaire</div>
        <div class="modal-body">
            <textarea class="form-control" bind:value={commentText} disabled={commentSaving} bind:this={commentElement}></textarea>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" disabled={commentSaving} onclick={() => closeComment()}>Annuler</button>
            <button type="button" class="btn btn-sm btn-primary" disabled={commentSaving || editComment.comment == commentText} onclick={() => saveComment()}>Sauvegarder</button>
        </div>
    </Modal>
{/if}

{#if editStatus}
    <Modal
        close={() => closeStatus()}
        keep={() => saveStatus()}
        modalClasses="modal-xl"
        mounted={() => {
            statusElement?.focus()
        }}
    >
        <div class="modal-header text-bg-primary">Statut</div>
        <div class="modal-body">
            <p>Status actuel de {editStatus.idna} : {editStatus.status}</p>
            <p>
                <label class="radio-inline"><input type="radio" class="radio radio-inline" bind:group={newStatus} value="pending" /> En attente (pending)</label>
            </p>
            <p>
                <label class="radio-inline"><input type="radio" class="radio radio-inline" bind:group={newStatus} value="done" /> Terminé (done)</label>
            </p>
            <p>
                <label class="radio-inline"><input type="radio" class="radio radio-inline" bind:group={newStatus} value="failed" /> En erreur (failed)</label>
            </p>
            <p>
                <label class="radio-inline"><input type="radio" class="radio radio-inline" bind:group={newStatus} value="canceled" /> Annulé (canceled)</label>
            </p>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" disabled={statusSaving} onclick={() => closeStatus()}>Annuler</button>
            <button type="button" class="btn btn-sm btn-primary" disabled={statusSaving || editStatus.status == newStatus} onclick={() => saveStatus()}>Sauvegarder</button>
        </div>
    </Modal>
{/if}
